<ng-container [ngSwitch]="view">
  <ng-container *ngSwitchCase="'main'" [ngTemplateOutlet]="mainContent"></ng-container>
  <ng-container *ngSwitchCase="'invite'" [ngTemplateOutlet]="inviteView"></ng-container>
</ng-container>

<!-- main view (selection), Step 1 -->
<ng-template #mainContent>
  <h1 mat-dialog-title class="tw-pt-1 tw-pb-3 md:tw-pb-4 md:tw-pt-2 tw-text-2xl">
    Share this view
  </h1>

  <button
    #closeDialogButton
    mat-icon-button
    mat-dialog-close
    class="tw-absolute tw-top-1 tw-right-1 tw-text-gray-200 hover:tw-text-gray-300">
    <mat-icon>close</mat-icon>
  </button>

  <mat-dialog-content class="tw-pt-0 tw-pb-6 md:tw-min-h-[486px]">
    <div
      id="main-container"
      class="tw-flex tw-flex-col md:tw-flex-row tw-gap-2 lg:tw-gap-3 tw-gap-y-6">
      <div
        class="tw-bg-gray-700 tw-rounded-md tw-p-4 md:tw-flex-1 tw-border tw-border-[#5252525c] tw-border-solid">
        <!-- Public link section -->
        <div id="public-container" class="tw-flex tw-flex-col tw-gap-4 tw-mb-4">
          <h2 class="tw-flex tw-items-center tw-gap-2 tw-my-0">
            <mat-icon class="tw-text-gray-300 tw-flex-shrink-0">public</mat-icon>
            <span class="tw-text-lg tw-text-gray-100">Public Share</span>
          </h2>

          <div>
            <h3 class="tw-text-base tw-text-gray-300 tw-mt-0 tw-mb-2 tw-font-medium">
              Create a public snapshot link
            </h3>

            <p class="tw-text-sm tw-text-gray-400 tw-mt-0 tw-mb-0">
              Anyone with this link can view a snapshot of the current page, including: <br />
              - for Insights pages: the currently selected timeframe, companies, filters, and chart
              configurations <br />
              - for People pages: the currently selected filters and filter values <br />
              - for Chat pages: all messages in the thread and generated artifacts
            </p>
          </div>

          <div class="tw-flex tw-flex-col tw-gap-2">
            <button
              *ngIf="!publicLink"
              mat-stroked-button
              color="primary"
              (click)="copyPublicLink()"
              [disabled]="isGeneratingPublicLink && !publicLink"
              class="tw-w-40">
              <span *ngIf="isGeneratingPublicLink && !publicLink">
                <mat-spinner diameter="20" class="tw-inline-block"></mat-spinner>
              </span>
              <span *ngIf="!isGeneratingPublicLink || publicLink">Copy public link</span>
            </button>

            <div
              *ngIf="publicLink"
              class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-bg-gray-600 tw-p-1 tw-pl-3 tw-rounded-md">
              <span class="tw-text-sm tw-text-gray-300 tw-truncate">
                {{ getUrlForAccessLink(publicLink) }}
              </span>
              <button mat-icon-button (click)="copyPublicLink()" matTooltip="Copy public link">
                <mat-icon>content_copy</mat-icon>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Private link & email invite section -->
      <div
        class="tw-bg-gray-700 tw-rounded-md tw-p-4 md:tw-flex-1 tw-border tw-border-[#5252525c] tw-border-solid">
        <div id="private-container" class="private-container tw-flex tw-flex-col tw-gap-4">
          <!-- Private link section -->
          <div class="tw-flex tw-flex-col tw-gap-4 tw-flex-wrap">
            <h2 class="tw-flex tw-items-center tw-gap-2 tw-my-0">
              <mat-icon class="tw-text-gray-300 tw-flex-shrink-0">link</mat-icon>
              <span class="tw-text-lg tw-text-gray-100">Private Share</span>
            </h2>

            <div>
              <h3 class="tw-text-base tw-text-gray-300 tw-mt-0 tw-mb-2 tw-font-medium">
                Create a private link
              </h3>

              <p class="tw-text-sm tw-text-gray-400 tw-mt-0 tw-mb-0">
                Only people that belong to your account can access this link.
              </p>
            </div>

            <div class="tw-flex tw-flex-col tw-gap-2">
              <button
                *ngIf="!privateLink"
                mat-stroked-button
                color="primary"
                (click)="copyPrivateLink()"
                [disabled]="isGeneratingPrivateLink && !privateLink"
                class="tw-w-40">
                <span *ngIf="isGeneratingPrivateLink && !privateLink">
                  <mat-spinner diameter="20" class="tw-inline-block"></mat-spinner>
                </span>
                <span *ngIf="!isGeneratingPrivateLink || privateLink">Copy private link</span>
              </button>

              <div
                *ngIf="privateLink"
                class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-bg-gray-600 tw-p-1 tw-pl-3 tw-rounded-md">
                <span class="tw-text-sm tw-text-gray-300 tw-truncate">
                  {{ getUrlForAccessLink(privateLink) }}
                </span>
                <button mat-icon-button (click)="copyPrivateLink()" matTooltip="Copy private link">
                  <mat-icon>content_copy</mat-icon>
                </button>
              </div>
            </div>
          </div>

          <div
            *ngIf="authService.$isOrgAdmin | async"
            class="tw-mt-4 tw-mb-2 tw-justify-center tw-hidden md:tw-flex">
            <mat-divider class="tw-w-1/2"></mat-divider>
          </div>

          <!-- Invite by email section -->
          <div
            *ngIf="authService.$isOrgAdmin | async"
            id="invite-by-email-container"
            class="tw-flex tw-flex-col tw-gap-4 tw-mt-6 md:tw-mt-0">
            <div>
              <h3 class="tw-text-base tw-text-gray-300 tw-mt-0 tw-mb-2 tw-font-medium">
                Invite users to your account
              </h3>

              <p class="tw-text-sm tw-text-gray-400 tw-mt-0 tw-mb-0">
                Invite people to your account to share views, quotas, and other data.
              </p>
            </div>

            <button mat-stroked-button color="primary" class="tw-w-40" (click)="showInviteView()">
              Invite people
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Admin PAL Cache Update button -->
    <div *ngIf="authService.$isAdmin | async" class="tw-flex tw-justify-center tw-mt-4">
      <app-access-link-cache-update></app-access-link-cache-update>
    </div>
  </mat-dialog-content>
</ng-template>

<!-- invite view, Step 2 -->
<ng-template #inviteView>
  <mat-dialog-content class="share-step-2 tw-pt-4 md:tw-min-h-[486px] tw-mt-4">
    <div class="tw-relative tw-flex tw-flex-col tw-gap-4">
      <div class="tw-flex tw-items-end tw-gap-4">
        <button mat-stroked-button (click)="view = 'main'">
          <mat-icon>arrow_back</mat-icon>
          Back
        </button>
        <h1 mat-dialog-title class="tw-pb-0 tw-mb-0 tw-pl-0">Invite people</h1>
      </div>

      <div class="tw-flex tw-flex-col tw-pt-1 tw-text-gray-400 tw-text-sm tw-gap-2 tw-px-4">
        <div>
          Start typing an email to invite. You can invite multiple people at once. Selecting an
          existing user will email them the link.
        </div>
      </div>

      <button
        #closeDialogButton
        mat-icon-button
        mat-dialog-close
        class="tw-absolute -tw-top-3 -tw-right-2 tw-text-gray-200 hover:tw-text-gray-300">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <!-- Invite by email popup -->
    <div
      id="invite-container"
      class="invite-container tw-flex tw-flex-col xs500:tw-flex-row tw-flex-wrap tw-gap-2 tw-mt-4 tw-max-w-[600px]">
      <div
        id="invite-form-container"
        class="invite-form-container tw-flex tw-gap-2 tw-w-full tw-pt-4">
        <mat-form-field appearance="outline" floatLabel="always" class="tw-flex-1 tw-mb-0">
          <mat-label>Enter email address</mat-label>
          <mat-chip-grid #chipGrid [ngClass]="{ 'tw-pb-4': selectedUsers.length > 0 }">
            <mat-chip-row
              *ngFor="let user of selectedUsers; trackBy: trackByUser"
              (removed)="removeSelectedUser(user)"
              [ngClass]="{ 'new-user-chip tw-bg-blue-600': !user.id, 'existing-user-chip dark:tw-bg-gray-700 dark:tw-border dark:tw-border-solid dark:tw-border-gray-500': user.id }">
              <div class="tw-flex tw-items-center tw-gap-2">
                <!-- if the user is new, show the invite tag -->
                <div
                  *ngIf="!user.id"
                  class="tw-flex tw-items-center tw-bg-gray-700 tw-bg-opacity-80 tw-rounded tw-px-2 tw-py-0.5 tw-text-white tw-text-sm">
                  Invite
                </div>

                <!-- if the user is not new, show the user avatar -->
                <div
                  *ngIf="user.id"
                  class="user-avatar user-avatar-small"
                  [style.background-color]="getAvatarColor(getSelectedUserName(user))">
                  {{ getSelectedUserInitials(user) }}
                </div>

                <span
                  class="tw-font-semibold tw-truncate tw-min-w-0 truncate-email"
                  [matTooltip]="getSelectedUserName(user)"
                  matTooltipPosition="above">
                  {{ getSelectedUserName(user) }}
                </span>

                <button matChipRemove>
                  <mat-icon>cancel</mat-icon>
                </button>
              </div>
            </mat-chip-row>
          </mat-chip-grid>
          <input
            #emailInputField
            matInput
            [matChipInputFor]="chipGrid"
            [formControl]="emailInput"
            placeholder="Invite by name or email"
            (keydown.enter)="onEnterKey($event)"
            autocomplete="off" />
          <button
            *ngIf="emailInput.value || selectedUsers.length > 0"
            matSuffix
            mat-icon-button
            aria-label="Clear and close"
            (click)="cancelUserSelection()">
            <mat-icon>close</mat-icon>
          </button>
          <mat-error *ngIf="emailInput.invalid">Please enter a valid email address</mat-error>
        </mat-form-field>

        <button
          mat-flat-button
          color="primary"
          [disabled]="selectedUsers.length === 0 || isSharing"
          (click)="share()"
          class="tw-h-14">
          <span *ngIf="!isSharing">Share</span>
          <mat-spinner *ngIf="isSharing" diameter="20" class="tw-mx-4"></mat-spinner>
        </button>
      </div>

      <!-- User list dropdown -->
      <div
        id="user-list-dropdown"
        class="tw-bg-gray-800 tw-border tw-border-gray-700 tw-border-solid tw-rounded tw-shadow-lg tw-z-50 user-list tw-min-h-[494px] tw-w-full tw-mt-4 sm:tw-mt-1">
        <div *ngIf="isLoadingUsers" class="tw-flex tw-justify-center tw-items-center tw-py-4">
          <mat-spinner diameter="24"></mat-spinner>
          <span class="tw-ml-3 tw-text-gray-300">Loading users...</span>
        </div>

        <!-- Valid email invite button -->
        <div
          *ngIf="!isLoadingUsers && filteredOrgUsers.length === 0 && isValidEmail(emailInput.value)"
          class="tw-p-4">
          <button
            mat-flat-button
            color="accent"
            class="tw-w-full tw-h-auto tw-overflow-hidden tw-py-3 tw-px-6"
            (click)="addNewEmail()">
            <span class="tw-flex tw-items-center tw-gap-2">
              <span class="material-icons tw-text-[20px] sm:tw-text-[24px]"> mail_outline </span>
              <span class="tw-text-sm sm:tw-text-base tw-font-medium tw-flex-wrap">
                Invite
                <span class="tw-font-bold">{{ emailInput.value }}</span>
              </span>
            </span>
          </button>

          <div
            *ngIf="!isLoadingUsers && filteredOrgUsers.length === 0 && !isValidEmail(emailInput.value)"
            class="tw-text-center tw-mt-8 tw-italic tw-text-gray-500">
            No people found. Keep typing a full email
          </div>
        </div>

        <!-- No results message -->
        <div
          *ngIf="!isLoadingUsers && filteredOrgUsers.length === 0 && !isValidEmail(emailInput.value)"
          class="tw-flex tw-justify-center tw-h-full">
          <div class="tw-text-center tw-p-6">
            <div class="tw-text-lg tw-text-gray-200 tw-bg-gray-600 tw-py-2 tw-px-4 tw-rounded-md">
              No people found. Keep typing email to invite
            </div>
            <div *ngIf="emailInput.value" class="tw-mt-8 tw-italic tw-text-gray-500">
              No existing users found matching "{{ emailInput.value }}"
            </div>
          </div>
        </div>

        <!-- User list -->
        <div *ngIf="!isLoadingUsers && filteredOrgUsers.length > 0">
          <div
            *ngFor="let user of filteredOrgUsers; trackBy: trackByUser"
            class="user-item tw-text-white hover:tw-bg-gray-700 tw-flex tw-items-center tw-p-2"
            (click)="toggleUserSelection(user)">
            <div class="user-avatar" [style.background-color]="getAvatarColor(getUserName(user))">
              {{ getUserInitials(user) }}
            </div>
            <div class="tw-flex tw-flex-col tw-flex-grow">
              <div class="tw-font-medium">{{ getUserName(user) }}</div>
              <div class="user-role tw-text-gray-400">
                {{ getUserEmail(user) }} • {{ getUserRole(user) }}
              </div>
            </div>
            <mat-icon *ngIf="isSelected(user)" class="tw-text-blue-500"> check_circle </mat-icon>
          </div>
        </div>
      </div>
    </div>
  </mat-dialog-content>
</ng-template>
